import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import './Login.css'

const PUBLIC_URL = process.env.PUBLIC_URL || ''
const logo = `${PUBLIC_URL}/images/logo.jpeg`
const accreditation = `${PUBLIC_URL}/images/acredited.jpeg`

const COMPANY_IMAGES = [
  `${PUBLIC_URL}/images/image1.jpeg`,
  `${PUBLIC_URL}/images/image2.jpeg`,
  `${PUBLIC_URL}/images/image3.jpeg`,
  `${PUBLIC_URL}/images/image4.jpeg`,
  `${PUBLIC_URL}/images/image5.jpeg`,
]

const CAPTIONS = [
  'Bunker survey operations',
  'On/Off-hire and condition surveys',
  'Draft surveys with accurate reporting',
  'SAC accredited inspection body',
  'Trusted by leading shipping clients',
]

const SERVICES = [
  { icon: 'bi-fuel-pump', title: 'Bunker Surveys' },
  { icon: 'bi-search', title: 'Bunker Detective Surveys' },
  { icon: 'bi-shield-check', title: 'Vetting Surveys' },
  { icon: 'bi-bag-check', title: 'Pre-Purchase Surveys' },
  { icon: 'bi-arrow-left-right', title: 'On/Off-Hire Surveys' },
  { icon: 'bi-rulers', title: 'Draft Surveys' },
  { icon: 'bi-clipboard-check', title: 'Condition Surveys' },
  { icon: 'bi-three-dots', title: 'and many more' },
]

const Login = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [slideIndex, setSlideIndex] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    let loaded = 0
    let anySuccess = false
    COMPANY_IMAGES.forEach((src) => {
      const img = new Image()
      img.onload = () => {
        anySuccess = true
        loaded += 1
        if (loaded === COMPANY_IMAGES.length) setImagesLoaded(anySuccess)
      }
      img.onerror = () => {
        loaded += 1
        if (loaded === COMPANY_IMAGES.length) setImagesLoaded(anySuccess)
      }
      img.src = src
    })
  }, [])

  // Restarts on every slide change so a manual pick gets the full 5s, matching the progress bar
  useEffect(() => {
    if (!imagesLoaded) return
    const timer = setTimeout(() => {
      setSlideIndex((i) => (i + 1) % COMPANY_IMAGES.length)
    }, 5000)
    return () => clearTimeout(timer)
  }, [imagesLoaded, slideIndex])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const result = await login(username, password)

    if (result.success) {
      navigate('/')
    } else {
      setError(result.error || 'Login failed')
    }

    setLoading(false)
  }

  return (
    <div className="login-jnj">
      <div className="login-jnj-orb login-jnj-orb-1" aria-hidden="true" />
      <div className="login-jnj-orb login-jnj-orb-2" aria-hidden="true" />

      <aside className="login-jnj-showcase" aria-label="About J and J Marine Surveys">
        <div className="login-jnj-media">
          {imagesLoaded ? (
            COMPANY_IMAGES.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                className={`login-jnj-media-img ${i === slideIndex ? 'active' : ''}`}
              />
            ))
          ) : (
            <div className="login-jnj-media-fallback" />
          )}
          <div className="login-jnj-media-shade" />
        </div>

        <div className="login-jnj-showcase-inner">
          <div className="login-jnj-accredit">
            <img src={accreditation} alt="SAC Accredited Inspection Body" />
          </div>

          <div className="login-jnj-story">
            <p className="login-jnj-eyebrow">Marine Survey Management</p>
            <h1 className="login-jnj-headline">
              Welcome aboard
              <span>J and J Marine</span>
            </h1>
            <p className="login-jnj-tagline">
              Survey scheduling, vessel operations, reporting and invoicing in one secure workspace.
            </p>

            {imagesLoaded && (
              <div className="login-jnj-slides">
                <p className="login-jnj-caption" aria-live="polite">
                  {CAPTIONS[slideIndex]}
                </p>
                <div className="login-jnj-progress">
                  {COMPANY_IMAGES.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`login-jnj-progress-seg ${i === slideIndex ? 'active' : ''} ${i < slideIndex ? 'done' : ''}`}
                      onClick={() => setSlideIndex(i)}
                      aria-label={`Slide ${i + 1}`}
                    >
                      <span />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <ul className="login-jnj-services">
              {SERVICES.map((s) => (
                <li key={s.title} className="login-jnj-service">
                  <i className={`bi ${s.icon}`} />
                  {s.title}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>

      <main className="login-jnj-form-panel">
        <div className="login-jnj-form-card">
          <div className="login-jnj-form-brand">
            <img src={logo} alt="J and J Marine Surveys" className="login-jnj-form-logo" />
          </div>

          <div className="login-jnj-form-heading">
            <h2 className="login-jnj-form-welcome">Welcome back</h2>
            <p className="login-jnj-form-sub">Sign in to your survey workspace</p>
          </div>

          {error && (
            <div className="login-jnj-error" role="alert">
              <i className="bi bi-exclamation-circle-fill" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="login-jnj-form-fields">
              <div>
                <label htmlFor="username" className="login-jnj-label">Username</label>
                <div className="login-jnj-input-wrap">
                  <i className="bi bi-person login-jnj-input-icon" />
                  <input
                    type="text"
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="login-jnj-input"
                    placeholder="Enter your username"
                    required
                    autoFocus
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="login-jnj-label">Password</label>
                <div className="login-jnj-input-wrap">
                  <i className="bi bi-lock login-jnj-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="login-jnj-input has-toggle"
                    placeholder="Enter your password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-jnj-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                  </button>
                </div>
              </div>

              <button type="submit" className="login-jnj-submit" disabled={loading}>
                <span className="login-jnj-submit-inner">
                  {loading ? (
                    <>
                      <span className="login-jnj-spinner" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-box-arrow-in-right" />
                      Sign In
                    </>
                  )}
                </span>
              </button>
            </div>
          </form>

          <footer className="login-jnj-form-footer">
            <a href="/public-password-change" className="login-jnj-form-link">
              <i className="bi bi-key" />
              Change Password
            </a>
            <span className="login-jnj-form-copy">
              © {new Date().getFullYear()} J and J Marine Surveys
            </span>
          </footer>
        </div>
      </main>
    </div>
  )
}

export default Login
